import tkUpgradeBear from '@/assets/error-bears/tk-upgrade-bear.webp';
import tkUpgradeBearRocket from '@/assets/error-bears/tk-upgrade-bear-rocket.webp';
import tkUpgradeBearPaint from '@/assets/error-bears/tk-upgrade-bear-paint.webp';
import tkErrorBearCables from '@/assets/error-bears/tk-error-bear-cables.webp';
import tkErrorBearRepair from '@/assets/error-bears/tk-error-bear-repair.webp';
import grokFreshDrop from '@/assets/error-bears/grok-a-fresh-drop.webp';
import grokRefreshStretch from '@/assets/error-bears/grok-b-refresh-stretch.webp';
import grokUpgradeCrown from '@/assets/error-bears/grok-c-upgrade-crown.webp';
import grokRealKnot from '@/assets/error-bears/grok-c-real-knot.webp';

export type FallbackLayout = 'bleed' | 'card';

export type UpgradeVariant = {
  id: string;
  layout: FallbackLayout;
  image: string;
  objectPosition: string;
  badge: string;
  heading: string;
  body: string;
  cta: string;
};

export type ErrorVariant = {
  id: string;
  image: string;
  objectPosition: string;
  badge: string;
  heading: string;
  body: string;
};

export const UPGRADE_VARIANTS: readonly UpgradeVariant[] = [
  {
    id: 'grok-a',
    layout: 'bleed',
    image: grokFreshDrop,
    objectPosition: '58% 42%',
    badge: 'New version',
    heading: 'TeddyKids just got an upgrade',
    body: "This tab is still on yesterday. One refresh and you're in the new one — nothing is broken.",
    cta: 'Open the new version',
  },
  {
    id: 'grok-b',
    layout: 'card',
    image: grokRefreshStretch,
    objectPosition: '50% 28%',
    badge: 'Nothing is broken',
    heading: 'Time for a refresh',
    body: 'We shipped a new version. This page just needs a reload so it can catch up.',
    cta: 'Reload',
  },
  {
    id: 'grok-c',
    layout: 'card',
    image: grokUpgradeCrown,
    objectPosition: '50% 28%',
    badge: 'New version',
    heading: 'Ready when you are',
    body: "TeddyKids just upgraded. Reload this tab and you're on the new version.",
    cta: 'Reload',
  },
  {
    id: 'claude-mechanic',
    layout: 'card',
    image: tkUpgradeBear,
    objectPosition: '50% 38%',
    badge: 'New version',
    heading: 'We just got an upgrade',
    body: "Teddy Kids LMS has a fresh new version. Give it a quick refresh and you're back in — nothing was lost.",
    cta: 'Refresh now',
  },
  {
    id: 'claude-rocket',
    layout: 'card',
    image: tkUpgradeBearRocket,
    objectPosition: '50% 40%',
    badge: 'Fresh delivery',
    heading: 'A new version just landed',
    body: "We shipped some improvements while you were working. One quick refresh and you'll have the newest Teddy Kids LMS.",
    cta: 'Refresh now',
  },
  {
    id: 'claude-paint',
    layout: 'card',
    image: tkUpgradeBearPaint,
    objectPosition: '50% 46%',
    badge: 'Just updated',
    heading: 'Fresh coat of paint',
    body: 'The app was just updated. Tap refresh to load the newest version — your work is safe.',
    cta: 'Refresh',
  },
];

export const ERROR_VARIANTS: readonly ErrorVariant[] = [
  {
    id: 'grok-knot',
    image: grokRealKnot,
    objectPosition: '50% 40%',
    badge: 'This one looks real',
    heading: 'Something actually broke',
    body: "Not an upgrade this time. Reload once — if it comes back, it's a real bug.",
  },
  {
    id: 'claude-cables',
    image: tkErrorBearCables,
    objectPosition: '50% 38%',
    badge: 'Something broke',
    heading: 'This page tripped over a cable',
    body: "Something genuinely went wrong loading this page — this one's on us. Reload once. If it comes back, it's a real bug.",
  },
  {
    id: 'claude-repair',
    image: tkErrorBearRepair,
    objectPosition: '50% 46%',
    badge: 'Real error',
    heading: 'Something needs a little repair',
    body: "This page hit a real error, not just an update. Reload once. If it comes back, it's a real bug.",
  },
];

export function pickRandom<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!;
}

export function findUpgrade(id: string | null): UpgradeVariant {
  return UPGRADE_VARIANTS.find((v) => v.id === id) ?? UPGRADE_VARIANTS[0]!;
}

export function findError(id: string | null): ErrorVariant {
  return ERROR_VARIANTS.find((v) => v.id === id) ?? ERROR_VARIANTS[0]!;
}
